import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";

import { SubscriptionResponseDto } from "api";

import Button from "components/Button";
import SubscriptionItemSelect from "../SubscriptionItemSelect";

import { PlanOption, SubscriptionCard } from "../variables";

import { ReactComponent as ListArrowIcon } from "assets/images/icon_list_arrow.svg";

import "./index.scss";

interface SubscriptionItemProps {
  card: SubscriptionCard;
  period?: string;
  customBtnText?: string;
  currentSubscription?: SubscriptionResponseDto | null;
  onChooseSubscription: (paymentPlanId?: number, modifierId?: string) => void;
}

const SubscriptionItem: React.FC<SubscriptionItemProps> = ({
  period = "місяць",
  card,
  customBtnText,
  currentSubscription,
  onChooseSubscription,
}) => {
  const findCurrentPlan = () => {
    if (currentSubscription) {
      const currentPlan = card.plans.find(
        (p) => p.paymentPlanId === currentSubscription.paymentPlan.paymentPlanId
      );
      if (currentPlan) {
        return currentPlan;
      }
    }
    return card.plans[0];
  };

  const [selectedPlan, setSelectedPlan] = useState<PlanOption>(findCurrentPlan);
  const [loading, setLoading] = useState(false);
  const [isAdvantagesOpen, setIsAdvantagesOpen] = useState(false);
  const { name, cardDescription, advantages, plans } = card;

  const isMobile = useMediaQuery("(max-width: 599px)");

  useEffect(() => {
    setSelectedPlan(findCurrentPlan());
  }, [currentSubscription, card.plans]);

  const isCurrent =
    !!currentSubscription &&
    selectedPlan?.paymentPlanId ===
      currentSubscription?.paymentPlan.paymentPlanId &&
    !currentSubscription?.isCancelled &&
    !currentSubscription?.isExpired;

  const isHighlighted =
    isCurrent ||
    (card.highlighted &&
      (!currentSubscription ||
        currentSubscription?.isTrial ||
        currentSubscription?.isCancelled));

  const containerClass = isHighlighted
    ? "highlighted-item"
    : "subscription-item";

  const handleSelectChange = (planId: string) => {
    const plan = card.plans.find((p) => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
    }
  };

  const handleToggleAdvantages = () => {
    setIsAdvantagesOpen((prev) => !prev);
  };

  const onClick = async () => {
    try {
      setLoading(true);

      const currentPlanId = selectedPlan?.paymentPlanId;

      await onChooseSubscription(currentPlanId, selectedPlan?.id);
    } catch (e) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      data-testid={`subs-card-${card.id}`}
      sx={{ pt: { xs: 2, md: isHighlighted ? 0 : 4.75 } }}
      width={{ xs: "100%", sm: "420px", md: "560px", lg: "380px" }}
    >
      <div className={containerClass}>
        {isHighlighted && (
          <div className="text-wrapper">
            <Typography variant="subtitle1" color="primary">
              {isCurrent ? "Поточний тариф" : "Найпопулярніший"}
            </Typography>
          </div>
        )}

        <div className="content">
          <Typography variant="h3" sx={{ mb: 1 }}>
            {name}
          </Typography>

          <Typography variant="body2" color="grey700" sx={{ mb: 1.5 }}>
            {cardDescription}
          </Typography>

          {selectedPlan?.id !== "custom" && (
            <Box display="flex" alignItems="flex-end" sx={{ mb: 4 }}>
              {selectedPlan?.oldPrice && (
                <Typography
                  variant="body2"
                  color="red700"
                  sx={{ mr: 1, textDecoration: "line-through" }}
                >
                  {selectedPlan?.oldPrice} грн/{period}
                </Typography>
              )}
              <Typography variant="h3">{selectedPlan?.price}</Typography>
              <Typography variant="body2" color="grey700" sx={{ ml: 1 }}>
                грн/{period}
              </Typography>
            </Box>
          )}

          <Button
            size="medium"
            variant="contained"
            color={isHighlighted ? "primary" : "secondary"}
            onClick={onClick}
            withBorder={!isHighlighted}
            disabled={isCurrent}
            loading={loading}
          >
            {(selectedPlan?.id === "custom" && customBtnText) || "Обрати"}
          </Button>

          <Box sx={{ mt: 4 }}>
            <Typography
              variant="body2"
              color="grey800"
              fontWeight="800"
              sx={{ mb: 1, display: { xs: "none", sm: "block" } }}
            >
              Що ви отримуєте:
            </Typography>

            {plans.length > 1 && (
              <SubscriptionItemSelect
                cardId={card.id}
                options={plans}
                selectedPlanId={selectedPlan?.id}
                onChange={handleSelectChange}
              />
            )}

            <Collapse in={!isMobile || isAdvantagesOpen}>
              {advantages.map((advantage, index) => (
                <Box key={index} display="flex" alignItems="center" mb={1}>
                  <ListArrowIcon />
                  <Typography variant="body2" color="grey800" sx={{ ml: 1 }}>
                    {advantage}
                  </Typography>
                </Box>
              ))}
            </Collapse>

            <Link
              component="button"
              onClick={handleToggleAdvantages}
              variant="subtitle2"
              color="grey700"
              sx={{ margin: "0 auto", display: { xs: "block", sm: "none" } }}
            >
              {isAdvantagesOpen ? "Згорнути список" : "Що ви отримуєте"}
            </Link>
          </Box>
        </div>
      </div>
    </Box>
  );
};

export default SubscriptionItem;
